/**
 * Coups de pouce — Attendu 664 « Définition de la bissectrice d'un angle saillant ».
 *
 * Une entrée par `generator_key` (cf. gen_664.js). Une entrée peut être un objet
 * `{ pages }` ou une fonction `(question) => ({ pages })` (cf. utils/coupDePouce.js).
 *
 * Règle absolue : l'aide porte sur d'AUTRES valeurs que celles que le
 * générateur peut tirer, sinon elle donne la réponse.
 * Emphase : **texte** (rendu en gras, pas de HTML brut). Pas de tiret cadratin.
 */

// -- Figure : a quoi ressemble une bissectrice --
// 76 et 38 : ANGLES va de 20 a 160 de dix en dix, 76 n'y est pas, et 38 n'est
// la moitie d'aucune de ces valeurs (les moities sont toutes multiples de 5).
// La figure ne peut donc pas coincider avec un tirage.
// -- Figure de la variante DEFINITION : aucune mesure ecrite --
// La bonne reponse de cette variante dit « en deux angles adjacents de meme
// mesure » : une legende qui reprend « meme mesure », ou des degres affiches,
// donneraient le critere mot pour mot. Seules les marques d’egalite le codent.
const SVG_BISSECTRICE_MUETTE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 190" role="img"
     aria-label="Un angle XVY partage par la demi-droite VT en deux angles superposables : VT est la bissectrice.">
  <line x1="80" y1="150" x2="210.0" y2="150.0" stroke="#0f172a" stroke-width="2.2"/>
  <line x1="80" y1="150" x2="111.4" y2="23.9" stroke="#0f172a" stroke-width="2.2"/>
  <line x1="80" y1="150" x2="182.4" y2="70.0" stroke="#1D9E75" stroke-width="2.8"/>
  <path d="M 125.0 150.0 A 45 45 0 0 0 115.5 122.3" fill="none" stroke="#F97316" stroke-width="2.2"/>
  <path d="M 115.5 122.3 A 45 45 0 0 0 90.9 106.3" fill="none" stroke="#F97316" stroke-width="2.2"/>
  <line x1="117.8" y1="137.0" x2="127.3" y2="133.7" stroke="#F97316" stroke-width="2"/>
  <line x1="101.8" y1="116.5" x2="107.2" y2="108.1" stroke="#F97316" stroke-width="2"/>
  <circle cx="80" cy="150" r="3.4" fill="#0f172a"/>
  <text x="219.0" y="155.0" font-size="13" font-weight="700" fill="#0f172a">X</text>
  <text x="102.4" y="19.9" text-anchor="end" font-size="13" font-weight="700" fill="#0f172a">Y</text>
  <text x="191.4" y="66.0" font-size="13" font-weight="700" fill="#1D9E75">T</text>
  <text x="70" y="168" text-anchor="end" font-size="13" font-weight="700" fill="#0f172a">V</text>
  <text x="250" y="120" font-size="12" font-weight="700" fill="#1D9E75">[VT) est la</text>
  <text x="250" y="138" font-size="12" font-weight="700" fill="#1D9E75">bissectrice</text>
  <text x="250" y="156" font-size="11" fill="#334155">de l’angle XVY</text>
  <text x="180" y="182" text-anchor="middle" font-size="11.5" font-weight="700" fill="#F97316">les deux angles obtenus sont superposables</text>
</svg>`.trim();

// -- Figure de la variante FIGURE : les mesures sont ecrites --
// 76 et 38 : ANGLES va de 20 a 160 de dix en dix, 76 n’y est pas, et 38 n’est
// la moitie d’aucune de ces valeurs. La figure ne peut pas coincider avec un
// tirage.
const SVG_BISSECTRICE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 190" role="img"
     aria-label="Un angle XVY partage par la demi-droite VT en deux angles egaux de 38 degres chacun : VT est la bissectrice.">
  <line x1="80" y1="150" x2="210.0" y2="150.0" stroke="#0f172a" stroke-width="2.2"/>
  <line x1="80" y1="150" x2="111.4" y2="23.9" stroke="#0f172a" stroke-width="2.2"/>
  <line x1="80" y1="150" x2="182.4" y2="70.0" stroke="#1D9E75" stroke-width="2.8"/>
  <path d="M 125.0 150.0 A 45 45 0 0 0 115.5 122.3" fill="none" stroke="#F97316" stroke-width="2.2"/>
  <path d="M 115.5 122.3 A 45 45 0 0 0 90.9 106.3" fill="none" stroke="#F97316" stroke-width="2.2"/>
  <line x1="117.8" y1="137.0" x2="127.3" y2="133.7" stroke="#F97316" stroke-width="2"/>
  <line x1="101.8" y1="116.5" x2="107.2" y2="108.1" stroke="#F97316" stroke-width="2"/>
  <circle cx="80" cy="150" r="3.4" fill="#0f172a"/>
  <text x="144.3" y="131.9" text-anchor="middle" font-size="12" font-weight="700" fill="#F97316">38&#176;</text>
  <text x="119.2" y="93.6" text-anchor="middle" font-size="12" font-weight="700" fill="#F97316">38&#176;</text>
  <text x="219.0" y="155.0" font-size="13" font-weight="700" fill="#0f172a">X</text>
  <text x="102.4" y="19.9" text-anchor="end" font-size="13" font-weight="700" fill="#0f172a">Y</text>
  <text x="191.4" y="66.0" font-size="13" font-weight="700" fill="#1D9E75">T</text>
  <text x="70" y="168" text-anchor="end" font-size="13" font-weight="700" fill="#0f172a">V</text>
  <text x="250" y="120" font-size="12" font-weight="700" fill="#1D9E75">[VT) est la</text>
  <text x="250" y="138" font-size="12" font-weight="700" fill="#1D9E75">bissectrice</text>
  <text x="250" y="156" font-size="11" fill="#334155">de l’angle XVY</text>
  <text x="180" y="182" text-anchor="middle" font-size="11.5" font-weight="700" fill="#F97316">les deux moitiés ont la même mesure</text>
</svg>`.trim();

// -- Figure du format RELIER : bissectrice contre mediatrice --
// La fiche ne liste AUCUNE definition, ce serait la reponse : le format
// consiste justement a apparier des termes a leurs definitions. Elle traite
// la seule confusion vraiment couteuse, bissectrice / mediatrice, et le fait
// par l'objet decoupe (un angle contre un segment), pas par une phrase.
const SVG_BISS_VS_MED = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 194" role="img"
     aria-label="A gauche la bissectrice d un angle, qui le coupe en deux angles superposables. A droite la mediatrice d un segment, qui le coupe en son milieu a angle droit.">
  <line x1="45" y1="120" x2="127.0" y2="120.0" stroke="#0f172a" stroke-width="2.2"/>
  <line x1="45" y1="120" x2="64.8" y2="40.4" stroke="#0f172a" stroke-width="2.2"/>
  <line x1="45" y1="120" x2="109.6" y2="69.5" stroke="#1D9E75" stroke-width="2.8"/>
  <path d="M 73.0 120.0 A 28 28 0 0 0 67.1 102.8" fill="none" stroke="#F97316" stroke-width="2"/>
  <path d="M 67.1 102.8 A 28 28 0 0 0 51.8 92.8" fill="none" stroke="#F97316" stroke-width="2"/>
  <line x1="67.7" y1="112.2" x2="75.3" y2="109.6" stroke="#F97316" stroke-width="2"/>
  <line x1="58.1" y1="99.9" x2="62.4" y2="93.2" stroke="#F97316" stroke-width="2"/>
  <circle cx="45" cy="120" r="3" fill="#0f172a"/>
  <text x="36" y="137" text-anchor="end" font-size="12.5" font-weight="700" fill="#0f172a">V</text>
  <line x1="270.0" y1="52" x2="270.0" y2="176" stroke="#378ADD" stroke-width="2.2" stroke-dasharray="7 5"/>
  <line x1="215" y1="120" x2="325" y2="120" stroke="#0f172a" stroke-width="2.4" stroke-linecap="round"/>
  <polyline points="282.0,120 282.0,108 270.0,108" fill="none" stroke="#F97316" stroke-width="1.8"/>
  <line x1="243" y1="114" x2="243" y2="126" stroke="#F97316" stroke-width="2"/>
  <line x1="297" y1="114" x2="297" y2="126" stroke="#F97316" stroke-width="2"/>
  <circle cx="215" cy="120" r="3.4" fill="#0f172a"/>
  <circle cx="325" cy="120" r="3.4" fill="#0f172a"/>
  <text x="207" y="137" text-anchor="end" font-size="12.5" font-weight="700" fill="#0f172a">R</text>
  <text x="333" y="137" font-size="12.5" font-weight="700" fill="#0f172a">S</text>
  <text x="90" y="165" text-anchor="middle" font-size="13" font-weight="700" fill="#1D9E75">bissectrice</text>
  <text x="90" y="183" text-anchor="middle" font-size="11.5" fill="#334155">elle coupe un angle</text>
  <text x="270" y="165" text-anchor="middle" font-size="13" font-weight="700" fill="#378ADD">médiatrice</text>
  <text x="270" y="183" text-anchor="middle" font-size="11.5" fill="#334155">elle coupe un segment</text>
</svg>`.trim();

// -- Figure du format RECONNAISSANCE VISUELLE : vraie contre fausse --
// Le format demande de trancher à l'œil, sans mesure. La fiche montre donc
// le critère visuel : les deux parts se superposent, ou non. Les deux arcs
// de couleurs differentes rendent la comparaison directe. Le decalage dessine
// vaut 20 degres, comme les ecarts du generateur (15, 18, 22, 25) : la fiche
// montre un ecart du meme ordre, ni plus facile ni plus dur a voir.
const SVG_VRAIE_FAUSSE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 196" role="img"
     aria-label="A gauche une demi-droite qui partage l angle en deux parts superposables : c est la bissectrice. A droite une demi-droite decalee, une part est plus ouverte que l autre.">
  <line x1="85" y1="105" x2="139.5" y2="85.2" stroke="#0f172a" stroke-width="2.2"/>
  <line x1="85" y1="105" x2="65.2" y2="50.5" stroke="#0f172a" stroke-width="2.2"/>
  <line x1="85" y1="105" x2="109.5" y2="52.4" stroke="#1D9E75" stroke-width="2.4"/>
  <path d="M 109.4 96.1 A 26 26 0 0 0 96.0 81.4" fill="none" stroke="#F97316" stroke-width="2"/>
  <path d="M 96.0 81.4 A 26 26 0 0 0 76.1 80.6" fill="none" stroke="#9333EA" stroke-width="2"/>
  <circle cx="85" cy="105" r="3" fill="#0f172a"/>
  <text x="85" y="152" text-anchor="middle" font-size="12.5" font-weight="700" fill="#1D9E75">bissectrice</text>
  <text x="85" y="170" text-anchor="middle" font-size="11" fill="#334155">les deux parts sont</text>
  <text x="85" y="185" text-anchor="middle" font-size="11" fill="#334155">superposables</text>
  <line x1="265" y1="105" x2="319.5" y2="85.2" stroke="#0f172a" stroke-width="2.2"/>
  <line x1="265" y1="105" x2="245.2" y2="50.5" stroke="#0f172a" stroke-width="2.2"/>
  <line x1="265" y1="105" x2="270.1" y2="47.2" stroke="#E24B4A" stroke-width="2.4"/>
  <path d="M 289.4 96.1 A 26 26 0 0 0 267.3 79.1" fill="none" stroke="#F97316" stroke-width="2"/>
  <path d="M 267.3 79.1 A 26 26 0 0 0 256.1 80.6" fill="none" stroke="#9333EA" stroke-width="2"/>
  <circle cx="265" cy="105" r="3" fill="#0f172a"/>
  <text x="265" y="152" text-anchor="middle" font-size="12.5" font-weight="700" fill="#E24B4A">pas la bissectrice</text>
  <text x="265" y="170" text-anchor="middle" font-size="11" fill="#334155">une part est plus</text>
  <text x="265" y="185" text-anchor="middle" font-size="11" fill="#334155">ouverte que l’autre</text>
  <line x1="175" y1="40" x2="175" y2="140" stroke="#E2E8F0" stroke-width="1"/>
</svg>`.trim();

// -- Figure du format NOMMER : lire le codage couleur --
// La figure de l'exercice code les angles egaux par la COULEUR des secteurs.
// La fiche montre comment la lire, sur un angle a trois demi-droites nommees
// P, Q, R : des lettres absentes de la figure de l'exercice, qui utilise A a F.
const SVG_CODAGE_COULEUR = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 196" role="img"
     aria-label="Trois demi-droites OP, OQ et OR. Les secteurs POQ et QOR portent la meme couleur, donc OQ est la bissectrice de l angle POR.">
  <path d="M 100 150 L 156.0 135.0 A 58 58 0 0 0 124.5 97.4 Z" fill="#fbbf24" fill-opacity="0.5" stroke="none"/>
  <path d="M 100 150 L 124.5 97.4 A 58 58 0 0 0 75.5 97.4 Z" fill="#fbbf24" fill-opacity="0.5" stroke="none"/>
  <line x1="100" y1="150" x2="191.8" y2="125.4" stroke="#0f172a" stroke-width="2.2"/>
  <line x1="100" y1="150" x2="140.1" y2="63.9" stroke="#0f172a" stroke-width="2.2"/>
  <line x1="100" y1="150" x2="59.9" y2="63.9" stroke="#0f172a" stroke-width="2.2"/>
  <circle cx="100" cy="150" r="3.4" fill="#0f172a"/>
  <text x="200.8" y="130.4" text-anchor="start" font-size="13" font-weight="700" fill="#0f172a">P</text>
  <text x="146.1" y="57.9" text-anchor="start" font-size="13" font-weight="700" fill="#0f172a">Q</text>
  <text x="51.9" y="57.9" text-anchor="end" font-size="13" font-weight="700" fill="#0f172a">R</text>
  <text x="90" y="168" text-anchor="end" font-size="13" font-weight="700" fill="#0f172a">O</text>
  <text x="222" y="96" font-size="11.5" font-weight="700" fill="#b45309">les deux secteurs</text>
  <text x="222" y="113" font-size="11.5" font-weight="700" fill="#b45309">ont la même couleur</text>
  <text x="222" y="140" font-size="11.5" font-weight="700" fill="#0f172a">donc [OQ) est la</text>
  <text x="222" y="157" font-size="11.5" font-weight="700" fill="#0f172a">bissectrice de PÔR</text>
</svg>`.trim();

const CP_664 = {
    // ENTREE FONCTION : le format alterne deux variantes. La variante DEFINITION
    // fait choisir la bonne phrase parmi quatre : lui donner la definition ecrite
    // serait lui donner la reponse, elle ne recoit donc que la figure. La variante
    // FIGURE demande ce qu'on peut affirmer d'un angle et de sa moitie : la regle
    // y donne le critere de verification, pas la reponse.
    // Variante « phrases » : l\'énoncé est une définition à reconnaître, la
    // figure muette suffit. Variante « figure » (défaut) : la question porte sur
    // un dessin, on rappelle les deux conditions.
    definition_bissectrice_qcm_unique: {
        variantes: [
            { sousType: 'phrases', pages: [{ titre: 'Un exemple', svg: SVG_BISSECTRICE_MUETTE }] },
        ],
        defaut: {
            pages: [
                {
                    titre: 'La règle',
                    lignes: [
                        'Une demi-droite est la **bissectrice** d\'un angle quand elle le partage en **deux angles égaux**.',
                        'Il faut donc vérifier que la demi-droite est à l\'**intérieur** de l\'angle et que sa mesure en est la **moitié**.',
                    ],
                },
                { titre: 'Un exemple', svg: SVG_BISSECTRICE },
            ],
        },
    },
    // Figure seule : la moindre définition écrite donnerait une des quatre paires
    // à relier. On montre ce que chaque terme découpe, l'élève retrouve les
    // formulations lui-même.
    definition_bissectrice_relier: {
        pages: [
            { titre: 'Un exemple', svg: SVG_BISS_VS_MED },
        ],
    },
    reconnaissance_visuelle_bissectrice_qcm_multi: {
        pages: [
            {
                titre: 'La règle',
                lignes: [
                    'La bissectrice partage l\'angle en **deux parts superposables** : en pliant le long d\'elle, un côté vient exactement sur l\'autre.',
                    'Compare donc les **deux parts** de chaque figure : si l\'une est plus ouverte que l\'autre, ce n\'est pas la bissectrice.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_VRAIE_FAUSSE },
        ],
    },
    nommer_bissectrices_qcm_multi: {
        pages: [
            {
                titre: 'La règle',
                lignes: [
                    'Une demi-droite est la **bissectrice** d\'un angle quand elle est **à l\'intérieur** de cet angle et qu\'elle le partage en deux parts de **même mesure**.',
                    'Sur une figure codée, repère les deux secteurs qui portent la **même couleur** : la demi-droite qui les sépare est la bissectrice de l\'angle qu\'ils forment ensemble.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_CODAGE_COULEUR },
        ],
    },
};

export default CP_664;
